import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getHawkStatus, hawkErrorMessage } from "../services/hawkReviewApi";
import type { HawkStatus } from "../services/hawkReviewApi";

/** Query param on links in Hawk approval emails; opens the dialog. */
export const HAWK_DIALOG_PARAM = "hawk";

type HawkLoad = {
  dealId: number;
  status: HawkStatus | null;
  error: string | null;
  loading: boolean;
};

/** Deal Menu label for each stage. */
export function hawkMenuLabel(status: HawkStatus | null): string {
  switch (status?.stage) {
    case "awaiting_deal_lead":
      return `Hawk approval: waiting for ${status.deal_lead?.name || "deal lead"}`;
    case "awaiting_asset_manager":
      return "Hawk approval: waiting for asset manager";
    case "ready_to_send":
      return "Send to Hawk";
    case "in_hawk":
      return "View in Hawk";
    default:
      return "Request Hawk approval";
  }
}

/**
 * Hawk approval status for a deal, plus the Send to Hawk dialog's open state.
 *
 * The status is keyed by deal so a previous deal's status never shows. The
 * dialog is open while `?hawk=1` is in the URL (email links) or after
 * `openDialog`; closing drops the param.
 */
export function useHawkFlow({
  dealId,
  enabled,
  dealClosed,
}: {
  dealId: number | undefined;
  /** Tenant `hawk_integration` flag. */
  enabled: boolean;
  /** From the loaded deal; only used when the status itself failed to load. */
  dealClosed: boolean;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [load, setLoad] = useState<HawkLoad | null>(null);
  const currentDealRef = useRef<number | null>(null);

  // Only sets state after the request resolves, so it is safe to start from an effect.
  const fetchStatus = useCallback(async () => {
    if (!dealId || !enabled) return;
    const requestedId = dealId;
    const response = await getHawkStatus(requestedId);
    // Ignore the response if the user has moved to another deal meanwhile
    if (currentDealRef.current !== requestedId) return;
    setLoad(
      response.success && response.data
        ? { dealId: requestedId, status: response.data, error: null, loading: false }
        : {
            dealId: requestedId,
            status: null,
            error: hawkErrorMessage(
              response,
              "Couldn't load the Hawk status for this deal.",
            ),
            loading: false,
          },
    );
  }, [dealId, enabled]);

  useEffect(() => {
    currentDealRef.current = dealId ?? null;
    void fetchStatus();
  }, [dealId, fetchStatus]);

  /** Retry and post-action reloads keep the current status visible meanwhile. */
  const reload = () => {
    if (!dealId) return;
    setLoad((prev) =>
      prev?.dealId === dealId ? { ...prev, error: null, loading: true } : prev,
    );
    void fetchStatus();
  };

  const current = load?.dealId === dealId ? load : null;
  const status = current?.status ?? null;
  const error = current?.error ?? null;
  const loading = enabled && (!current || current.loading);

  const closeDialog = () => {
    setDialogOpen(false);
    if (searchParams.get(HAWK_DIALOG_PARAM) !== null) {
      const next = new URLSearchParams(searchParams);
      next.delete(HAWK_DIALOG_PARAM);
      setSearchParams(next, { replace: true });
    }
  };

  // Open pipeline deals don't offer Hawk; a failed status load still gets an
  // entry so the dialog can show the error and retry.
  const showMenuItem =
    enabled &&
    (status
      ? status.deal_closed || status.stage !== "not_started"
      : !!error && dealClosed);

  return {
    status,
    error,
    loading,
    reload,
    dialogOpen:
      enabled &&
      (dialogOpen || searchParams.get(HAWK_DIALOG_PARAM) === "1"),
    openDialog: () => setDialogOpen(true),
    closeDialog,
    menuLabel: hawkMenuLabel(status),
    showMenuItem,
  };
}
